import { ASTERISK_API_BASE, TRANSCRIPTION_API_BASE } from '../config/bridge';

const trimValue = (value) => String(value ?? '').trim();

const buildAuthHeaders = (headers = {}) => {
  const nextHeaders = {
    Accept: 'application/json',
    ...headers
  };
  const token = localStorage.getItem('token');
  if (token) {
    nextHeaders.Authorization = `Bearer ${token}`;
  }
  return nextHeaders;
};

const readJsonResponse = async (response) => {
  const text = await response.text();
  let payload = {};

  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = { message: text };
  }

  if (!response.ok) {
    const error = new Error(
      payload?.error ||
      payload?.detail ||
      payload?.message ||
      text ||
      `Request failed (${response.status})`
    );
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
};

const buildLookupQuery = (lookup = {}, extra = {}) => {
  const params = new URLSearchParams();
  Object.entries({ ...lookup, ...extra }).forEach(([key, value]) => {
    const normalized = trimValue(value);
    if (normalized) {
      params.set(key, normalized);
    }
  });
  return params.toString();
};

const inferRecordingName = (call = {}) => {
  const explicitName = trimValue(call?.RecordingName || call?.recordingName);
  return explicitName;
};

const getCallType = (call = {}) => trimValue(call?.CallType || call?.callType).toLowerCase();

const isVoicemailCall = (call = {}) => (
  trimValue(call?.CallTypeGroup || call?.callTypeGroup).toLowerCase() === 'voicemail' ||
  getCallType(call).includes('voicemail') ||
  Boolean(call?.IsVoicemail || call?.isVoicemail || call?.Voicemail || call?.voicemail) ||
  trimValue(call?.Source || call?.source).toLowerCase().includes('voicemail')
);

const normalizeRecordingCall = (call = {}) => ({
  ...call,
  recordingName: inferRecordingName(call)
});

const buildRecordingDedupeKey = (call = {}) => {
  const recordingName = trimValue(call?.RecordingName || call?.recordingName).toLowerCase();
  if (recordingName) return `recording:${recordingName}`;

  const uniqueId = trimValue(call?.UniqueId || call?.uniqueId).toLowerCase();
  if (uniqueId) return `unique:${uniqueId}`;

  const voicemailId = trimValue(call?.Id || call?.id);
  if (voicemailId && isVoicemailCall(call)) return `voicemail:${voicemailId}`;

  return `fallback:${JSON.stringify(call)}`;
};

const mergeRecordingCalls = (...groups) => {
  const merged = new Map();

  groups.flat().forEach((call) => {
    if (!call || typeof call !== 'object') return;
    const normalized = normalizeRecordingCall(call);
    const key = buildRecordingDedupeKey(normalized);
    if (!merged.has(key)) {
      merged.set(key, normalized);
      return;
    }

    merged.set(key, {
      ...normalized,
      ...merged.get(key),
      RecordingPath: merged.get(key)?.RecordingPath || normalized.RecordingPath,
      RecordingUrl: merged.get(key)?.RecordingUrl || normalized.RecordingUrl,
      recordingName: merged.get(key)?.recordingName || normalized.recordingName
    });
  });

  return Array.from(merged.values());
};

export const buildRecordingLookupFromCall = (call = {}) => ({
  recordingName: inferRecordingName(call),
  uniqueId: trimValue(call?.UniqueId || call?.uniqueId),
  linkedId: trimValue(call?.LinkedId || call?.linkedId),
  channelId: trimValue(call?.ChannelId || call?.channelId),
  queryId: trimValue(call?.QueryId || call?.queryId),
  callerNumber: trimValue(call?.CallerNumber || call?.callerNumber),
  callerName: trimValue(call?.CallerName || call?.callerName),
  recordingPath: trimValue(call?.RecordingPath || call?.recordingPath),
  recordingUrl: trimValue(call?.RecordingUrl || call?.recordingUrl),
  recordingType: trimValue(call?.RecordingType || call?.recordingType || call?.CallType || call?.callType),
  callTypeGroup: trimValue(call?.CallTypeGroup || call?.callTypeGroup),
  source: trimValue(call?.Source || call?.source),
  isVoicemail: isVoicemailCall(call) ? 'true' : ''
});

export const queueCallTranscript = async (lookup = {}, options = {}) => {
  const response = await fetch(`${TRANSCRIPTION_API_BASE}/call-transcript`, {
    method: 'POST',
    headers: buildAuthHeaders({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify({
      ...lookup,
      force: Boolean(options.force)
    })
  });

  return readJsonResponse(response);
};

export const fetchCallTranscript = async (lookup = {}, options = {}) => {
  const query = buildLookupQuery(lookup, {
    start: options.start ? 'true' : '',
    force: options.force ? 'true' : ''
  });
  const url = `${TRANSCRIPTION_API_BASE}/call-transcript${query ? `?${query}` : ''}`;
  const response = await fetch(url, {
    headers: buildAuthHeaders()
  });

  return readJsonResponse(response);
};

export const fetchAgentRecordings = async (filters = {}) => {
  const query = buildLookupQuery({}, {
    agentId: filters.agentId,
    from: filters.from,
    to: filters.to,
    callerNumber: filters.callerNumber,
    limit: filters.limit || 50
  });
  const response = await fetch(`${ASTERISK_API_BASE}/call-analytics${query ? `?${query}` : ''}`, {
    headers: buildAuthHeaders()
  });
  const payload = await readJsonResponse(response);

  return {
    ...payload,
    source: 'analytics',
    calls: Array.isArray(payload?.calls) ? payload.calls : []
  };
};

export const fetchRecordingAudioObjectUrl = async (recording = {}) => {
  const query = buildLookupQuery(buildRecordingLookupFromCall(recording));
  if (!query) {
    throw new Error('Recording file is not available.');
  }

  const response = await fetch(`${TRANSCRIPTION_API_BASE}/recording-audio?${query}`, {
    headers: buildAuthHeaders()
  });

  if (!response.ok) {
    await readJsonResponse(response);
  }

  const blob = await response.blob();
  if (!blob.size) {
    throw new Error('Recording audio is empty.');
  }

  return URL.createObjectURL(blob);
};

const pad = (value) => String(value).padStart(2, '0');

export const formatTranscriptTime = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '';

  const wholeSeconds = Math.floor(seconds);
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const remainingSeconds = wholeSeconds % 60;

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  }

  return `${pad(minutes)}:${pad(remainingSeconds)}`;
};

export const getTranscriptSpeakerRoster = (record = {}) => {
  const speakerSegments = Array.isArray(record.speakerSegments)
    ? record.speakerSegments
    : Array.isArray(record.speaker_segments)
      ? record.speaker_segments
      : [];

  const speakers = [];
  const seen = new Set();

  speakerSegments.forEach((segment) => {
    const speakerId = trimValue(segment.speakerId || segment.speaker_id);
    if (!speakerId || seen.has(speakerId)) return;
    seen.add(speakerId);
    speakers.push({
      speakerId,
      label: `Speaker ${speakers.length + 1}`
    });
  });

  return speakers;
};

export const buildTranscriptEntries = (record = {}, options = {}) => {
  const speakerRoster = getTranscriptSpeakerRoster(record);
  const explicitAgentSpeakerId = trimValue(options.agentSpeakerId);
  const hasExplicitAgent = Boolean(
    explicitAgentSpeakerId &&
    speakerRoster.some((speaker) => speaker.speakerId === explicitAgentSpeakerId)
  );
  const defaultLabelById = new Map(
    speakerRoster.map((speaker, index) => [speaker.speakerId, speaker.label || `Speaker ${index + 1}`])
  );
  const speakerSegments = Array.isArray(record.speakerSegments)
    ? record.speakerSegments
    : Array.isArray(record.speaker_segments)
      ? record.speaker_segments
      : [];

  if (speakerSegments.length) {
    const speakerMap = new Map();
    const shouldDefaultToCallRoles = !hasExplicitAgent && speakerRoster.length === 2;

    return speakerSegments
      .map((segment, index) => {
        const speakerId = trimValue(segment.speakerId || segment.speaker_id) || `speaker-${index + 1}`;
        const text = trimValue(
          segment.text ||
          (Array.isArray(segment.words)
            ? segment.words.map((word) => trimValue(word.word)).filter(Boolean).join(' ')
            : '')
        );

        if (!text) return null;

        if (!speakerMap.has(speakerId)) {
          const speakerIndex = speakerMap.size;
          const isAgent = hasExplicitAgent && speakerId === explicitAgentSpeakerId;
          const isCaller = hasExplicitAgent && !isAgent && speakerRoster.length === 2;
          const isDefaultCaller = shouldDefaultToCallRoles && speakerIndex === 0;
          const isDefaultAgent = shouldDefaultToCallRoles && speakerIndex === 1;
          speakerMap.set(speakerId, {
            speakerLabel:
              isAgent || isDefaultAgent
                ? 'Agent'
                : isCaller || isDefaultCaller
                  ? 'Caller'
                  : (defaultLabelById.get(speakerId) || `Speaker ${speakerIndex + 1}`),
            align:
              isAgent || isDefaultAgent
                ? 'end'
                : isCaller || isDefaultCaller
                  ? 'start'
                  : speakerIndex % 2 === 0
                    ? 'start'
                    : 'end'
          });
        }

        const speakerMeta = speakerMap.get(speakerId);
        return {
          id: `${speakerId}-${index + 1}`,
          speaker: speakerMeta.speakerLabel,
          speakerLabel: speakerMeta.speakerLabel,
          text,
          timestamp: formatTranscriptTime(segment.start),
          align: speakerMeta.align
        };
      })
      .filter(Boolean);
  }

  const segments = Array.isArray(record.segments) ? record.segments : [];
  if (segments.length) {
    return segments
      .map((segment, index) => {
        const text = trimValue(segment.text);
        if (!text) return null;
        return {
          id: `segment-${index + 1}`,
          speaker: 'Transcript',
          speakerLabel: 'Transcript',
          text,
          timestamp: formatTranscriptTime(segment.start),
          align: 'start'
        };
      })
      .filter(Boolean);
  }

  const fullText = trimValue(record.text);
  if (!fullText) return [];

  return [
    {
      id: 'transcript-full',
      speaker: 'Transcript',
      speakerLabel: 'Transcript',
      text: fullText,
      timestamp: '',
      align: 'start'
    }
  ];
};

export const describeTranscriptStatus = (record = {}, fallback = '') => {
  const status = trimValue(record.status).toLowerCase();

  if (status === 'completed') return 'Transcript ready';
  if (status === 'processing') return 'The recording is being transcribed...';
  if (status === 'pending') return 'The transcript is queued...';
  if (status === 'failed') {
    return trimValue(record.error) || 'The transcript could not be generated.';
  }

  return fallback;
};

const normalizeSummaryText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const summarizeTranscriptLine = (value, maxLength = 150) => {
  const cleaned = normalizeSummaryText(value)
    .replace(/^[\s,.;:!?-]+/, '')
    .replace(/[\s,.;:!?-]+$/, '');
  if (!cleaned) return '';
  if (cleaned.length <= maxLength) return cleaned;

  const slice = cleaned.slice(0, maxLength);
  const safeSlice = slice.includes(' ') ? slice.slice(0, slice.lastIndexOf(' ')) : slice;
  return `${safeSlice.trim()}...`;
};

const normalizeSummarySpeaker = (value) => {
  const normalized = trimValue(value).toLowerCase();
  if (normalized.includes('agent')) return 'agent';
  if (normalized.includes('caller')) return 'caller';
  if (normalized.includes('system')) return 'system';
  if (normalized.includes('transcript')) return 'other';
  return normalized || 'other';
};

export const buildTranscriptSummary = (entries = []) => {
  const spokenEntries = (Array.isArray(entries) ? entries : [])
    .map((entry, index) => ({
      id: entry?.id || `entry-${index + 1}`,
      speaker: trimValue(entry?.speakerLabel || entry?.speaker || 'Transcript'),
      speakerType: normalizeSummarySpeaker(entry?.speakerLabel || entry?.speaker),
      text: summarizeTranscriptLine(entry?.text),
      timestamp: trimValue(entry?.timestamp),
      align: trimValue(entry?.align)
    }))
    .filter((entry) => entry.text && entry.speakerType !== 'system');

  if (!spokenEntries.length) {
    return {
      hasContent: false,
      overview: '',
      callerHighlights: [],
      agentHighlights: [],
      otherHighlights: [],
      keyMoments: []
    };
  }

  const mergedEntries = [];
  spokenEntries.forEach((entry) => {
    const last = mergedEntries[mergedEntries.length - 1];
    if (last && last.speakerType === entry.speakerType && last.speaker === entry.speaker) {
      const combined = summarizeTranscriptLine(`${last.text}. ${entry.text}`, 220);
      last.text = combined || last.text;
      if (!last.timestamp && entry.timestamp) {
        last.timestamp = entry.timestamp;
      }
      return;
    }

    mergedEntries.push({ ...entry });
  });

  const collectHighlights = (speakerType) => {
    const seen = new Set();
    return mergedEntries
      .filter((entry) => entry.speakerType === speakerType)
      .map((entry) => entry.text)
      .filter((text) => {
        const key = text.toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 3);
  };

  const callerHighlights = collectHighlights('caller');
  const agentHighlights = collectHighlights('agent');
  const otherHighlights = collectHighlights('other');
  const keyMoments = mergedEntries.slice(0, 6).map((entry) => ({
    id: entry.id,
    speaker: entry.speaker,
    speakerType: entry.speakerType,
    text: entry.text,
    timestamp: entry.timestamp
  }));

  const overviewParts = [];
  if (callerHighlights[0]) {
    overviewParts.push(`Caller: ${callerHighlights[0]}.`);
  }
  if (agentHighlights[0]) {
    overviewParts.push(`Agent: ${agentHighlights[0]}.`);
  }
  if (!overviewParts.length && otherHighlights[0]) {
    overviewParts.push(`Main transcript point: ${otherHighlights[0]}.`);
  }

  return {
    hasContent: true,
    overview: overviewParts.join(' '),
    callerHighlights,
    agentHighlights,
    otherHighlights,
    keyMoments
  };
};
